import md5 from 'md5';
import { parse } from 'qs';
import Store from 'repatch';

import State from '../core/State';
import AmazonAdAccount from '../entity/AmazonAdAccount';
import ErrorPresenter from '../service/ErrorPresenter';
import MessageDispatcher from '../service/MessageDispatcher';
import AmazonAdWebApi, { UploadCreativeUploadedFileInvalidError } from '../service/persistent/AmazonAdWebApi';
import {
  finishIntegratedAmazonCommunication,
  setAmazonAdAccounts,
  setAmazonAdAccountTokenId,
  setAmazonUploadedCreative,
  setIntegratedAmazonAdAccounts,
  startIntegratedAmazonAdAccounts,
} from '../thunk/amazonAdAccount';
import { setUploadedAdPlatformKeys } from '../thunk/creativeSending';
import {
  decrement as decrementFullScreenSpinnerNecessity,
  increment as incrementFullScreenSpinnerNecessity,
  setLoadingMessage,
} from '../thunk/fullScreenSpinner';
import { setLastAccessedOrganization } from '../thunk/lastAccessedOrganization';
import getPkceInfo from '../utility/getPkceInfo';
import { OrganizationDetailFragment } from './../generated/graphql';

class IntegrateAmazonAdAccountUsecase {
  static readonly CODE_VERIFIER_KEY = 'amazon-collector-code-verifier';
  private store: Store<State>;
  private errorPresenter: ErrorPresenter;
  private messageDispatcher: MessageDispatcher;
  private amazonAdWebApi: AmazonAdWebApi;

  constructor({
    store,
    errorPresenter,
    amazonAdWebApi,
    messageDispatcher,
  }: {
    store: Store<State>;
    errorPresenter: ErrorPresenter;
    amazonAdWebApi: AmazonAdWebApi;
    messageDispatcher: MessageDispatcher;
  }) {
    this.store = store;
    this.errorPresenter = errorPresenter;
    this.amazonAdWebApi = amazonAdWebApi;
    this.messageDispatcher = messageDispatcher;
  }

  // @TODO HOST_URLとredirectURIとclint_idは別に定義する
  public fetchAd = async () => {
    const state: State = this.store.getState();

    const query = parse(state.location.search.slice(1));
    const organizationId: string | null = query.org || null;
    const csrf = md5(new Date().getTime().toString());

    window.localStorage.setItem('amazon-collector-csrf', csrf);
    window.localStorage.setItem('amazon-collector-target-orgId', organizationId || '');
    const pkceInfo = await getPkceInfo();
    window.localStorage.setItem(IntegrateAmazonAdAccountUsecase.CODE_VERIFIER_KEY, pkceInfo.verifier);

    window.location.href = `https://www.amazon.com/ap/oa?client_id=amzn1.application-oa2-client.e68157bcae694ba68e3009eb940187e7&scope=cpc_advertising:campaign_management+profile&response_type=code&redirect_uri=${window.location.origin}&state=${csrf}&code_challenge_method=S256&code_challenge=${pkceInfo.challenge}`;
  };

  /**
   * AmazonAdAccount連携のログイン後、リダイレクトによってページ遷移してきた時のみ呼ばれる
   */
  // @dev-commenT: done FIX
  public fetchAccessibleAmazonAdAccounts = async (): Promise<boolean | void> => {
    if (location.href.match(/\?amazonAdAuthCode=/)) {
      const query = parse(location.search.substr(1));
      const authCode = query.amazonAdAuthCode;
      if (authCode) {
        this.store.dispatch(incrementFullScreenSpinnerNecessity());
        try {
          const tokenId = await this.amazonAdWebApi.registerAmazonAccount(authCode, query.org, this.getVerifier());
          this.store.dispatch(setAmazonAdAccountTokenId(tokenId));
          // 連携可能なアカウント一覧
          const accessibleAmazonAdAccounts = await this.amazonAdWebApi.getAllAccessibleAmazonAdAccounts(
            this.getCurrentOrganization().id,
            tokenId,
          );

          this.store.dispatch(setAmazonAdAccounts(accessibleAmazonAdAccounts));
          if (accessibleAmazonAdAccounts.size === 0) {
            this.messageDispatcher.notifyUnimportant('message.amazon_no_linkable_ad_account');
          }
          return true;
        } catch (err) {
          this.errorPresenter.showAsNonFatal(err);
          throw err;
        } finally {
          this.store.dispatch(decrementFullScreenSpinnerNecessity());
        }
      }
    }
  };

  public integrateAmazonAdAccount = async (amazonAdAccount: AmazonAdAccount): Promise<void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());
    try {
      const state: State = this.store.getState();
      const tokenId = state.integratedAmazonAdAccounts.tokenId;
      this.store.dispatch(setLoadingMessage('message.amazon_now_integrating'));
      const isSuccess = await this.amazonAdWebApi.integrateAdAccount(
        amazonAdAccount.profileId,
        tokenId,
        this.getCurrentOrganization().id,
        amazonAdAccount.countryCode,
      );
      if (isSuccess) {
        this.messageDispatcher.notifyUnimportant('message.amazon_integrated');
      } else {
        this.errorPresenter.showAsNonFatal(new Error('message.amazon_integrated_failed'));
      }
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
    } finally {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
    }
    this.fetchIntegratedAmazonAdAccounts();
  };

  // @dev-comment: done FIX
  public fetchIntegratedAmazonAdAccounts = async (orgId?: string): Promise<boolean | void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());
    this.store.dispatch(startIntegratedAmazonAdAccounts());

    try {
      const integratedAmazonAdAccounts = await this.amazonAdWebApi.getAllIntegratedAdAccounts(
        orgId || this.getCurrentOrganization().id,
      );
      this.store.dispatch(setIntegratedAmazonAdAccounts(integratedAmazonAdAccounts));
      return true;
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
    } finally {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
    }
  };

  public fetchUploadedAmazonCreatives = async (creativeId: string, orgId?: string): Promise<boolean | void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());

    try {
      const uploadedAmazonCreatives = await this.amazonAdWebApi.getUploadedAmazonCreatives(
        orgId || this.getCurrentOrganization().id,
        [creativeId],
      );
      this.store.dispatch(setAmazonUploadedCreative(!!uploadedAmazonCreatives.length));
      return !!uploadedAmazonCreatives.length;
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
    } finally {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
    }
  };

  // @dev-comment: done fix
  public revokeAmazonAdAccount = async (amazonAdAccount: AmazonAdAccount): Promise<void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());
    this.store.dispatch(startIntegratedAmazonAdAccounts());
    try {
      const isSuccess = await this.amazonAdWebApi.revokeAdAccount(
        amazonAdAccount.profileId,
        this.getCurrentOrganization().id,
      );
      if (isSuccess) {
        this.messageDispatcher.notifyUnimportant('message.amazon_revoked');
      } else {
        this.errorPresenter.showAsNonFatal(new Error('message.amazon_revoked_failed'));
      }
      this.store.dispatch(finishIntegratedAmazonCommunication());
    } catch (err) {
      this.errorPresenter.showAsFatal(err);
    } finally {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
    }
    this.fetchIntegratedAmazonAdAccounts();
  };

  // 連携amazonアカウントにcreativeを送信する
  public sendCreative = async (
    deliverable: {
      id: string;
      creative: { id: string };
    },
    amazonAdAccount: AmazonAdAccount,
    projectId: string | null,
  ): Promise<void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());

    if (projectId) {
      try {
        this.store.dispatch(setLoadingMessage('message.creative_now_sending'));

        await this.amazonAdWebApi.sendCreativeToAmazon(
          deliverable.creative.id,
          this.getCurrentOrganization().id,
          projectId,
          amazonAdAccount.profileId,
        );

        this.messageDispatcher.notifyUnimportant('message.creative_has_sent_to_amazon_ad_asset_library', {
          action: 'message.creative_has_sent_to_amazon_ad_asset_library.action',
          onClickAction: () => this.transitionToAmazonAdAssetLibrary(amazonAdAccount),
        });

        const uploadedAdPlatformKeys =
          this.store.getState().creativeSending.uploadedAdPlatformKeys.get(deliverable.id) || [];
        this.store.dispatch(setUploadedAdPlatformKeys(deliverable.id, [...uploadedAdPlatformKeys, 'amazon']));
      } catch (err) {
        if (err instanceof UploadCreativeUploadedFileInvalidError) {
          // 動画のレギュレーション違反
          this.messageDispatcher.notifyUnimportant('message.amazon_upload_creative-uploaded_file_invalid', {
            action: 'message.amazon_upload_creative-uploaded_file_invalid.action',
            onClickAction: () => this.transitionToAmazonAdPolicy(),
          });
        } else {
          this.errorPresenter.showAsNonFatal(err);
        }
      } finally {
        this.store.dispatch(decrementFullScreenSpinnerNecessity());
      }
    }
  };

  private transitionToAmazonAdPolicy = () => {
    window.open('https://advertising.amazon.com/resources/ad-policy/sponsored-ads-policies#sbv', '_blank');
  };

  private transitionToAmazonAdAssetLibrary = (amazonAdAccount: AmazonAdAccount) => {
    const redirectURL =
      amazonAdAccount.countryCode === 'CA'
        ? 'https://advertising.amazon.ca/cm/drafts'
        : 'https://advertising.amazon.com/cm/drafts';
    window.open(`${redirectURL}`, '_blank');
  };

  // @dev-comment: done FIX
  private getCurrentOrganization(): OrganizationDetailFragment {
    const state: State = this.store.getState();

    let organization: OrganizationDetailFragment | null = null;
    if (state.lastAccessedOrganization) {
      organization = state.lastAccessedOrganization;
    } else {
      organization = state.availableOrganizations.items[0];
    }
    if (!organization) {
      throw Error('organization was not found');
    }
    this.store.dispatch(setLastAccessedOrganization(organization));
    return organization;
  }

  private getVerifier(): string {
    const verifier = window.localStorage.getItem(IntegrateAmazonAdAccountUsecase.CODE_VERIFIER_KEY);
    if (verifier) {
      window.localStorage.removeItem(IntegrateAmazonAdAccountUsecase.CODE_VERIFIER_KEY);
      return verifier;
    }
    throw new Error('failed to verifier');
  }
}

export default IntegrateAmazonAdAccountUsecase;
